import { stateFromSnapshot } from '../game/cosmos.js';
import { snapshotStateToDataUrl } from '../game/renderer.js';

const TABS = [
  { id: 'top', label: 'TOP ♥' },
  { id: 'recent', label: 'RÉCENTS' },
  { id: 'mine', label: 'À MOI' }
];

/**
 * Shared universe gallery (all players, guests included): like ranking,
 * newest, or my own creations. Click a tile to display the universe
 * full-screen on the canvas behind the grid.
 * Thumbnails are rendered client-side from the snapshots (nothing binary
 * transits through the API).
 */
export default class GalleryScreen {
  constructor({ onBack, onSelect, onTab, onLike }) {
    this.onBack = onBack;
    this.onSelect = onSelect;
    this.onTab = onTab;
    this.onLike = onLike;
    this.gridEl = document.getElementById('gallery-grid');
    this.emptyEl = document.getElementById('gallery-empty');
    this.tabsEl = document.getElementById('gallery-tabs');
    this.tab = 'top';
    this._thumbCache = new Map(); // id/name+date -> data URL

    document.getElementById('btn-gallery-back')?.addEventListener('click', () => this.onBack?.());
    this._renderTabs();
  }

  _renderTabs() {
    if (!this.tabsEl) return;
    this.tabsEl.textContent = '';
    for (const { id, label } of TABS) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'difficulty-switch__option';
      button.setAttribute('role', 'radio');
      button.dataset.tab = id;
      button.textContent = label;
      button.addEventListener('click', () => {
        if (this.tab === id) return;
        this.tab = id;
        this._syncTabs();
        this.onTab?.(id);
      });
      this.tabsEl.appendChild(button);
    }
    this._syncTabs();
  }

  _syncTabs() {
    if (!this.tabsEl) return;
    for (const button of this.tabsEl.children) {
      const active = button.dataset.tab === this.tab;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-checked', String(active));
    }
  }

  _thumbFor(entry) {
    const key = entry.id ?? `${entry.name}|${entry.createdAt}`;
    if (!this._thumbCache.has(key)) {
      // Local entries may carry a stored thumb; shared ones are re-rendered.
      const stored = entry.data?.thumb;
      this._thumbCache.set(
        key,
        stored || snapshotStateToDataUrl(stateFromSnapshot(entry.data), 220)
      );
      if (this._thumbCache.size > 200) this._thumbCache.clear();
    }
    return this._thumbCache.get(key);
  }

  /**
   * @param {Array} universes shared payload: { id, name, era, data, username, likes, likedByMe, createdAt }
   */
  render(universes, { tab = this.tab } = {}) {
    if (!this.gridEl) return;
    this.tab = tab;
    this._syncTabs();
    this.gridEl.textContent = '';
    this.emptyEl?.classList.toggle('hidden', universes.length > 0);

    universes.forEach((entry, index) => {
      const wrap = document.createElement('div');
      wrap.className = 'gallery-tile';
      wrap.setAttribute('role', 'listitem');

      const tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'gallery-tile__main';

      const thumb = this._thumbFor(entry);
      if (thumb) {
        const img = document.createElement('img');
        img.src = thumb;
        img.alt = '';
        img.loading = 'lazy';
        tile.appendChild(img);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'gallery-tile__void';
        placeholder.textContent = '✦';
        tile.appendChild(placeholder);
      }

      const name = document.createElement('strong');
      name.textContent = entry.name;

      const meta = document.createElement('span');
      const date = new Date(entry.createdAt);
      meta.textContent = `${entry.era} · ${date.toLocaleDateString()}`;

      const author = document.createElement('span');
      author.className = 'gallery-tile__author';
      author.textContent = entry.username ? `PAR ${entry.username}` : '';

      tile.append(name, meta, author);
      tile.addEventListener('click', () => {
        this.gridEl.querySelectorAll('.gallery-tile__main.is-active').forEach((el) => el.classList.remove('is-active'));
        tile.classList.add('is-active');
        this.onSelect?.(entry.data ?? entry);
      });

      wrap.appendChild(tile);

      // Like: only for shared universes (server id) — guests can like too.
      if (entry.id != null) {
        const like = document.createElement('button');
        like.type = 'button';
        like.className = 'gallery-tile__like';
        like.setAttribute('aria-label', `Aimer ${entry.name}`);
        this._paintLike(like, entry.likedByMe, entry.likes);
        like.addEventListener('click', async (event) => {
          event.stopPropagation();
          like.disabled = true;
          const updated = await this.onLike?.(entry.id);
          if (updated) {
            entry.likedByMe = updated.liked;
            entry.likes = updated.likes;
            this._paintLike(like, updated.liked, updated.likes);
          }
          like.disabled = false;
        });
        wrap.appendChild(like);
      }

      if (index === 0) tile.classList.add('is-active');
      this.gridEl.appendChild(wrap);
    });
  }

  _paintLike(button, liked, likes) {
    button.textContent = `${liked ? '♥' : '♡'} ${likes}`;
    button.classList.toggle('is-liked', !!liked);
  }
}
